import { AUTH_CONFIG } from '../../config/auth';
import {
  FullAuthProvider,
  OAuthAuthProvider,
  AuthUser,
  AuthSession,
  AuthResponse,
} from './providerInterface';
import { apiService } from '../apiService';

// Firebase imports
import { initializeApp, FirebaseApp } from 'firebase/app';
import {
  getAuth,
  Auth,
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  Unsubscribe,
  GoogleAuthProvider as FirebaseGoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  sendEmailVerification,
  reload,
} from 'firebase/auth';
import { authLogger } from '../../utils/loggerInstance';

// Email verification interfaces
interface EmailVerificationResult {
  success: boolean;
  message: string;
  verificationLink?: string;
}

interface EmailVerificationStatus {
  verified: boolean;
  message: string;
}

/**
 * ============================================================================
 * PROVIDER SELECTION GUIDE
 * ============================================================================
 *
 * This file exports TWO auth providers that share the same Firebase configuration:
 *
 * 1. 🔐 googleIdentityPlatformProvider (FullAuthProvider)
 *    - Use for: Complete authentication solution
 *    - Supports: Email/password + Google OAuth
 *    - Best for: Main authentication flows, user registration/login
 *
 * 2. 🔗 googleAuthProvider (OAuthAuthProvider)
 *    - Use for: OAuth-only authentication
 *    - Supports: Google OAuth only (no email/password methods)
 *    - Best for: OAuth-specific integrations, social login components
 *
 * SHARED CONFIGURATION: Both providers intentionally share the same Firebase
 * Auth instance to ensure consistent user state and seamless interoperability.
 * Users authenticated through either provider are recognized as the same user.
 * ============================================================================
 */

// Initialize Firebase app
let firebaseApp: FirebaseApp | null = null;
let auth: Auth | null = null;

const initializeFirebase = (): Auth => {
  if (!firebaseApp) {
    const config = {
      apiKey: AUTH_CONFIG.GOOGLE_IDENTITY_PLATFORM.API_KEY,
      authDomain: AUTH_CONFIG.GOOGLE_IDENTITY_PLATFORM.AUTH_DOMAIN,
      projectId: AUTH_CONFIG.GOOGLE_IDENTITY_PLATFORM.PROJECT_ID,
    };

    // Debug: Log what we actually got
    console.error('🔥 Firebase Config Debug:', {
      apiKey: config.apiKey ? '***SET***' : 'MISSING',
      authDomain: config.authDomain || 'MISSING',
      projectId: config.projectId || 'MISSING',
      rawAuthConfig: AUTH_CONFIG.GOOGLE_IDENTITY_PLATFORM,
    });

    // Validate configuration
    if (!config.apiKey || !config.authDomain || !config.projectId) {
      console.error('🚨 Firebase config validation failed:', config);
      throw new Error(
        'Firebase configuration is incomplete. Please set VITE_FIREBASE_API_KEY, VITE_FIREBASE_AUTH_DOMAIN, and VITE_FIREBASE_PROJECT_ID environment variables.',
      );
    }

    firebaseApp = initializeApp(config);
  }

  if (!auth) {
    auth = getAuth(firebaseApp);
  }

  return auth;
};

// Helper function to convert Firebase User to AuthUser
const mapFirebaseUser = (user: User | null): AuthUser | null => {
  if (!user) return null;

  return {
    id: user.uid,
    email: user.email || '',
    email_confirmed_at: user.emailVerified ? new Date().toISOString() : null,
    created_at: user.metadata.creationTime || new Date().toISOString(),
    updated_at: user.metadata.lastSignInTime || new Date().toISOString(),
    user_metadata: {
      email: user.email || '',
      email_verified: user.emailVerified,
      phone_verified: !!user.phoneNumber,
    },
    app_metadata: {
      provider: 'google_identity_platform',
      providers: ['google_identity_platform'],
    },
  };
};

// Helper function to create AuthSession from Firebase user and ID token
const createAuthSession = async (user: User): Promise<AuthSession> => {
  try {
    // Validate user object before proceeding
    if (!user) {
      throw new Error('Invalid user object provided to createAuthSession');
    }

    authLogger.info('Creating auth session for user', { userId: user.uid });
    const tokenResult = await user.getIdTokenResult();

    if (!tokenResult || !tokenResult.token) {
      throw new Error('Failed to retrieve valid ID token from Firebase user');
    }

    const idToken = tokenResult.token;

    return {
      access_token: idToken,
      expires_at: tokenResult.claims.exp
        ? Number(tokenResult.claims.exp)
        : Math.floor(Date.now() / 1000) + 3600, // Use expiration time from the token with fallback
      refresh_token: user.refreshToken,
      user: mapFirebaseUser(user) || undefined,
    };
  } catch (error) {
    authLogger.error('Failed to create authentication session', error);

    // Provide more specific error messages based on the error type
    if (error instanceof Error) {
      if (error.message.includes('auth/network-request-failed')) {
        throw new Error(
          'Network error occurred while creating session. Please check your connection and try again.',
        );
      } else if (error.message.includes('auth/user-token-expired')) {
        throw new Error('Your authentication has expired. Please sign in again.');
      } else if (error.message.includes('auth/user-disabled')) {
        throw new Error('Your account has been disabled. Please contact support.');
      }
    }

    // Generic fallback error message
    throw new Error('Unable to create authentication session. Please try signing in again.');
  }
};

/**
 * Base class containing common Firebase auth methods to eliminate duplication
 */
abstract class BaseGoogleProvider {
  protected readonly auth: Auth;

  constructor() {
    this.auth = initializeFirebase();
  }

  abstract getProviderName(): string;

  async handleRedirectResult(): Promise<AuthResponse> {
    // This method is intentionally a no-op because popup-based authentication flows
    // do not rely on redirect results. The authentication process completes within
    // the popup itself, so there is no redirect result to handle.
    return {
      data: { user: null, session: null },
      error: null,
    };
  }

  async signOut(): Promise<{ error: Error | null }> {
    try {
      await signOut(this.auth);
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }

  async getUser(): Promise<{ data: { user: AuthUser | null } }> {
    try {
      const user = this.auth.currentUser;
      if (!user) {
        return {
          data: { user: null },
        };
      }

      const authUser = mapFirebaseUser(user);
      return {
        data: { user: authUser },
      };
    } catch (error) {
      return {
        data: { user: null },
      };
    }
  }

  async getSession(): Promise<{ data: { session: AuthSession | null } }> {
    try {
      const user = this.auth.currentUser;
      if (!user) {
        return {
          data: { session: null },
        };
      }

      const session = await createAuthSession(user);
      return {
        data: { session },
      };
    } catch (error) {
      return {
        data: { session: null },
      };
    }
  }

  async refreshSession(): Promise<AuthResponse> {
    try {
      const user = this.auth.currentUser;
      if (!user) {
        return {
          data: { user: null, session: null },
          error: new Error('No user found'),
        };
      }

      // Force refresh the ID token
      await user.getIdToken(true);
      const session = await createAuthSession(user);
      const authUser = mapFirebaseUser(user);

      return {
        data: { user: authUser, session },
        error: null,
      };
    } catch (error) {
      return {
        data: { user: null, session: null },
        error: error as Error,
      };
    }
  }

  /**
   * Generic OAuth authentication method to eliminate duplication
   */
  protected async signInWithOAuthCommon(
    providerType: 'google' | 'microsoft',
    _providerName: string,
    authFailureMessage: string,
    logErrorMessage: string,
  ): Promise<AuthResponse> {
    try {
      let provider: FirebaseGoogleAuthProvider | OAuthProvider;

      if (providerType === 'google') {
        provider = new FirebaseGoogleAuthProvider();
        provider.addScope('email');
        provider.addScope('profile');
      } else {
        provider = new OAuthProvider('microsoft.com');
        provider.addScope('email');
        provider.addScope('profile');
        provider.addScope('openid');
      }

      const userCredential = await signInWithPopup(this.auth, provider);
      const user = userCredential.user;

      if (!user) {
        return {
          data: { user: null, session: null },
          error: new Error(authFailureMessage),
        };
      }

      const session = await createAuthSession(user);
      const authUser = mapFirebaseUser(user);

      return {
        data: { user: authUser, session },
        error: null,
      };
    } catch (error: unknown) {
      authLogger.error(logErrorMessage, error);
      return {
        data: { user: null, session: null },
        error: error as Error,
      };
    }
  }

  protected async signInWithGoogleCommon(): Promise<AuthResponse> {
    return this.signInWithOAuthCommon(
      'google',
      'Google',
      'Google OAuth authentication failed',
      'Google OAuth error',
    );
  }

  protected async signInWithMicrosoftCommon(): Promise<AuthResponse> {
    return this.signInWithOAuthCommon(
      'microsoft',
      'Microsoft',
      'Microsoft authentication failed',
      'Microsoft sign-in error',
    );
  }
}

class GoogleIdentityPlatformProvider extends BaseGoogleProvider implements FullAuthProvider {
  getProviderName(): string {
    return 'google_identity_platform';
  }

  async signInWithGoogle(): Promise<AuthResponse> {
    // Use the common implementation from BaseGoogleProvider
    return this.signInWithGoogleCommon();
  }

  async signInWithMicrosoft(): Promise<AuthResponse> {
    // Use the common implementation from BaseGoogleProvider
    return this.signInWithMicrosoftCommon();
  }

  async handleRedirectResult(): Promise<AuthResponse> {
    // This method is intentionally a no-op because popup-based authentication flows
    // do not rely on redirect results. The authentication process completes within
    // the popup itself, so there is no redirect result to handle.
    return {
      data: { user: null, session: null },
      error: null,
    };
  }

  async signInWithPassword(email: string, password: string): Promise<AuthResponse> {
    try {
      const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
      const user = userCredential.user;

      if (!user) {
        return {
          data: { user: null, session: null },
          error: new Error('Authentication failed'),
        };
      }

      const session = await createAuthSession(user);
      const authUser = mapFirebaseUser(user);

      return {
        data: { user: authUser, session },
        error: null,
      };
    } catch (error) {
      return {
        data: { user: null, session: null },
        error: error as Error,
      };
    }
  }

  async signUpWithPassword(email: string, password: string): Promise<AuthResponse> {
    try {
      const userCredential = await createUserWithEmailAndPassword(this.auth, email, password);
      const user = userCredential.user;

      if (!user) {
        return {
          data: { user: null, session: null },
          error: new Error('Registration failed'),
        };
      }

      const session = await createAuthSession(user);
      const authUser = mapFirebaseUser(user);

      return {
        data: { user: authUser, session },
        error: null,
      };
    } catch (error) {
      return {
        data: { user: null, session: null },
        error: error as Error,
      };
    }
  }

  // signOut, getUser, getSession, and refreshSession are inherited from BaseGoogleProvider
  // No need to override them as they work the same for both providers

  onAuthStateChange(callback: (event: string, session: AuthSession | null) => void): Unsubscribe {
    return onAuthStateChanged(this.auth, async (user: User | null) => {
      if (user) {
        try {
          const session = await createAuthSession(user);
          callback('SIGNED_IN', session);
        } catch (error) {
          authLogger.error('Error creating session', error);
          callback('SIGNED_OUT', null);
        }
      } else {
        callback('SIGNED_OUT', null);
      }
    });
  }

  /**
   * Send email verification to the current user
   */
  async sendEmailVerification(): Promise<EmailVerificationResult> {
    try {
      const user = this.auth.currentUser;

      if (!user) {
        return {
          success: false,
          message: 'No user is currently signed in',
        };
      }

      if (user.emailVerified) {
        return {
          success: true,
          message: 'Email is already verified',
        };
      }

      await sendEmailVerification(user);

      authLogger.info('Email verification sent successfully');

      return {
        success: true,
        message: 'Email verification sent successfully. Please check your inbox.',
      };
    } catch (error: unknown) {
      authLogger.error('Failed to send email verification', { error });

      const code =
        typeof error === 'object' && error !== null && 'code' in error
          ? String((error as { code?: unknown }).code)
          : '';

      // Handle specific Firebase errors
      if (code === 'auth/too-many-requests') {
        return {
          success: false,
          message: 'Too many requests. Please wait before requesting another verification email.',
        };
      } else if (code === 'auth/user-disabled') {
        return {
          success: false,
          message: 'Your account has been disabled. Please contact support.',
        };
      }

      return {
        success: false,
        message: 'Failed to send email verification. Please try again.',
      };
    }
  }

  /**
   * Resend email verification to the current user
   */
  async resendEmailVerification(): Promise<EmailVerificationResult> {
    authLogger.info('Resending email verification');
    return await this.sendEmailVerification();
  }

  /**
   * Check if the current user's email is verified
   */
  async checkEmailVerificationStatus(): Promise<EmailVerificationStatus> {
    try {
      const user = this.auth.currentUser;

      if (!user) {
        return {
          verified: false,
          message: 'No user is currently signed in',
        };
      }

      // Reload user to get fresh verification status
      await reload(user);

      // If verified in Firebase, sync with our backend database
      if (user.emailVerified && user.email) {
        try {
          const response = await apiService.post('/api/v1/auth/email-verification/manual', {
            email: user.email,
          });

          if (response.status === 200) {
            const result = response.data;
            authLogger.info('Synced email verification status with backend:', result);
          } else {
            authLogger.warn('Failed to sync verification status with backend');
          }
        } catch (syncError: unknown) {
          authLogger.warn('Error syncing verification status with backend:', { error: syncError });
          // Don't fail the whole operation if sync fails
        }
      }

      return {
        verified: user.emailVerified,
        message: user.emailVerified ? 'Email is verified' : 'Email is not verified',
      };
    } catch (error: unknown) {
      authLogger.error('Failed to check email verification status', error);

      return {
        verified: false,
        message: 'Failed to check verification status',
      };
    }
  }

  /**
   * Wait for email verification to complete
   * This method polls the verification status until verified or timeout
   */
  async waitForEmailVerification(timeoutMs: number = 300000): Promise<EmailVerificationStatus> {
    const startTime = Date.now();
    const pollInterval = 2000; // Check every 2 seconds

    while (Date.now() - startTime < timeoutMs) {
      const status = await this.checkEmailVerificationStatus();

      if (status.verified) {
        return status;
      }

      // Wait before next poll
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
    }

    return {
      verified: false,
      message: 'Email verification timeout. Please check your email and try again.',
    };
  }
}

// ============================================================================
// EXPORTED PROVIDER INSTANCES
// ============================================================================

/**
 * 🔐 FULL AUTHENTICATION PROVIDER
 *
 * Main provider for complete Firebase authentication capabilities.
 *
 * @supports Email/password authentication, Google OAuth, session management
 * @use_when You need complete auth functionality (most common use case)
 * @example
 * ```ts
 * // For login/register screens
 * await googleIdentityPlatformProvider.signInWithPassword(email, password);
 * await googleIdentityPlatformProvider.signInWithGoogle();
 * ```
 */
export const googleIdentityPlatformProvider = new GoogleIdentityPlatformProvider();

// ============================================================================
// GOOGLE OAUTH PROVIDER (Dedicated Google OAuth Authentication)
// ============================================================================

/**
 * Dedicated Google OAuth Provider
 *
 * This provider is specifically designed for Google OAuth authentication
 * using Firebase's Google Auth Provider. It's separate from the general
 * Google Identity Platform provider to maintain clean separation of concerns.
 *
 * Note: This provider implements only OAuthAuthProvider, so it doesn't have
 * email/password methods that would just return errors.
 */
class GoogleAuthProvider extends BaseGoogleProvider implements OAuthAuthProvider {
  getProviderName(): string {
    return 'google_oauth';
  }

  async signInWithGoogle(): Promise<AuthResponse> {
    return this.signInWithGoogleCommon();
  }

  async signInWithMicrosoft(): Promise<AuthResponse> {
    // Use the common implementation from BaseGoogleProvider
    return this.signInWithMicrosoftCommon();
  }

  // Note: No email/password methods needed since this implements OAuthAuthProvider only
  // All common methods (handleRedirectResult, signOut, getUser, getSession, refreshSession)
  // are inherited from BaseGoogleProvider

  onAuthStateChange(callback: (event: string, session: AuthSession | null) => void): Unsubscribe {
    return onAuthStateChanged(this.auth, async (user: User | null) => {
      if (user) {
        try {
          const session = await createAuthSession(user);
          callback('SIGNED_IN', session);
        } catch (error) {
          authLogger.error('Error creating session', error);
          callback('SIGNED_OUT', null);
        }
      } else {
        callback('SIGNED_OUT', null);
      }
    });
  }
}

/**
 * 🔗 OAUTH-ONLY PROVIDER
 *
 * Specialized provider for Google OAuth authentication only.
 *
 * @supports Google OAuth only (no email/password methods exposed)
 * @use_when You need OAuth-only flows with type safety
 * @benefits Prevents accidental use of email/password methods
 * @example
 * ```ts
 * // For OAuth-specific components
 * await googleAuthProvider.signInWithGoogle();
 * // googleAuthProvider.signInWithPassword() // ❌ Not available (compile-time safety)
 * ```
 */
export const googleAuthProvider = new GoogleAuthProvider();
